import { put, takeLatest, select } from 'redux-saga/effects'
import * as Eff from 'redux-saga/effects' // tslint:disable-line:no-duplicate-imports
import toastr from 'toastr'
import 'toastr/build/toastr.css'

import Logger from '@Utils/Logger'

import { ISuccessResponse, IAction } from '@Root/types'

import {
  fetchLabelsFailure,
  fetchLabelsSuccess,
  createLabelFailure,
  createLabelSuccess,
  createProjectLabelsFailure,
  createProjectLabelsSuccess,
  deleteProjectLabelsFailure,
  deleteProjectLabelsSuccess,
  renameLabelSuccess,
  renameLabelFailure,
  deleteLabelSuccess,
  deleteLabelFailure,
  createProjectLabels,
  setLabelSelection,
} from './actions'

import {
  LabelActionTypes,
  IFetchLabelsSuccessResponseBody,
  ICreateLabelPayload,
  ICreateProjectLabelsPayload,
  IDeleteProjectLabelsPayload,
  IFetchLabelsPayload,
  IRenameLabelPayload,
  IDeleteLabelPayload,
  ICreateLabelSuccessResponseBody,
  ISetLabelSelectionPayload,
} from './types'

import {
  closeLabelPopups,
} from '@SiteModules/Popup/actions'

import {
  fetchProjects
} from '@SiteModules/Project/actions'

import * as API from './api'

const call: any = Eff.call

function* fetchLabelsSaga(action: IAction<IFetchLabelsPayload>) {
  try {
    const { payload } = action
    const response: ISuccessResponse<IFetchLabelsSuccessResponseBody> = yield call(API.fetchLabels, payload)
    const payloadResponse = response.data
    yield put(fetchLabelsSuccess(payloadResponse))
  } catch (e) {
    Logger.error(e, 'Labels could not be loaded')
    yield put(fetchLabelsFailure())
  }
}

function* createLabelSaga(action: IAction<ICreateLabelPayload>) {
  try {
    const { payload } = action
    const response: ISuccessResponse<ICreateLabelSuccessResponseBody> = yield call(API.createLabel, payload)

    const payloadResponse = response.data
    yield put(createLabelSuccess(payloadResponse))
    yield put(closeLabelPopups())
    if (payload.projectIdsForAssignment) {
      const labelId = parseInt(payloadResponse.data.id, 10)
      yield put(createProjectLabels({ selectedProjectIds: payload.projectIdsForAssignment, labelId } ))
      toastr.success('', 'Label created and assigned')
    } else {
      toastr.success('', 'Label created')
    }
  } catch (e) {
    Logger.error(e, 'Label could not be created')
    yield put(createLabelFailure())
  }
}

function* createProjectLabelsSaga(action: IAction<ICreateProjectLabelsPayload>) {
  try {
    const { payload } = action
    yield call(API.createProjectLabels, payload)
    yield put(createProjectLabelsSuccess(payload))
  } catch (e) {
    Logger.error(e, 'Labels could not be updated')
    yield put(createProjectLabelsFailure())
  }
}

function* deleteProjectLabelsSaga(action: IAction<IDeleteProjectLabelsPayload>) {
  try {
    const { payload } = action
    yield call(API.deleteProjectLabels, payload)
    yield put(deleteProjectLabelsSuccess(payload))
  } catch (e) {
    Logger.error(e, 'Labels could not be updated')
    yield put(deleteProjectLabelsFailure())
  }
}

function* renameLabelSaga(action: IAction<IRenameLabelPayload>) {
  try {
    const { payload } = action
    yield call(API.renameLabel, payload)
    yield put(renameLabelSuccess(payload))
    yield put(closeLabelPopups())
    toastr.success('', 'Label is renamed!')
  } catch (e) {
    Logger.error(e, 'Label could not be renamed!')
    yield put(renameLabelFailure())
  }
}

function* deleteLabelSaga(action: IAction<IDeleteLabelPayload>) {
  try {
    const { payload } = action
    yield call(API.deleteLabel, payload)
    yield put(deleteLabelSuccess(payload))
    yield put(closeLabelPopups())
    yield put(setLabelSelection({ labelIds: [] }))
    toastr.success('', 'Label is deleted!')
  } catch (e) {
    Logger.error(e, 'Label could not be deleted!')
    yield put(deleteLabelFailure())
  }
}

function isSetLabelSelectionAction(action: IAction<any>): action is IAction<ISetLabelSelectionPayload> {
  return action.type === LabelActionTypes.SET_LABEL_SELECTION
}

function* changeOrSetSelectedLabelsSaga(action: IAction<any>): Generator<any, any, any> {
  try {
    if (isSetLabelSelectionAction(action) && action.payload.suppressProjectsFetch) {
      return
    }

    const state = yield select()
    const searchText = state.projects.searchText
    const payloadFetchProjects = {
      selectedLabels: state.labels.selectedLabelIds,
      workspaceId: state.workspaces.currentWorkspaceId,
      page: 1,
      ...(searchText.length > 0 ? { searchText } : {}),
    }

    yield put(fetchProjects(payloadFetchProjects))
  } catch (e) {
    Logger.error(e, 'Cannot change label selection')
  }
}

function* labelSagas() {
  yield takeLatest(LabelActionTypes.FETCH_LABELS, fetchLabelsSaga)
  yield takeLatest(LabelActionTypes.CREATE_LABEL, createLabelSaga)
  yield takeLatest(LabelActionTypes.DELETE_PROJECT_LABELS, deleteProjectLabelsSaga)
  yield takeLatest(LabelActionTypes.CREATE_PROJECT_LABELS, createProjectLabelsSaga)
  yield takeLatest(LabelActionTypes.RENAME_LABEL, renameLabelSaga)
  yield takeLatest(LabelActionTypes.DELETE_LABEL, deleteLabelSaga)
  yield takeLatest(LabelActionTypes.CHANGE_SELECTED_LABELS, changeOrSetSelectedLabelsSaga)
  yield takeLatest(LabelActionTypes.SET_LABEL_SELECTION, changeOrSetSelectedLabelsSaga)
}

export default labelSagas
